import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Media, SnakeToCamelObject } from '@browsing/api';

import { Campaign } from '@shared/types/mylounge';
import { TimeLeft } from '@client/components/shared/timeLeft';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { buildCampaignUrl } from '@client/helpers/campaignUrl';

import { useInView } from 'react-intersection-observer';
import * as S from './campaign-card-mini.styles';
import Discount from './discount';

interface CampaignCardMiniProps {
    campaign: Campaign;
    media: Omit<SnakeToCamelObject<Media>, 'path'>;
    hideDiscount?: boolean;
    hideTimeLeft?: boolean;
    bgTransparent?: boolean;
    isPlusVariant?: boolean;
    onClick?: () => void;
    onView?: () => void;
}

const CampaignCardMini = ({
    campaign,
    media,
    hideDiscount = false,
    hideTimeLeft = false,
    bgTransparent = false,
    isPlusVariant = false,
    onClick = () => {},
    onView = () => {},
}: CampaignCardMiniProps) => {
    const { campaignId } = campaign;
    const dateEnd = useMemo(() => new Date(campaign.endsAt).getTime(), [campaign.endsAt]);
    const categoryTabId = useSelector(getCategoryTabId);
    const destination = buildCampaignUrl(campaign.campaignId, categoryTabId);

    const { ref, inView } = useInView({
        threshold: 0.5,
        triggerOnce: true,
    });

    useEffect(() => {
        if (inView && onView) {
            onView();
        }
    }, [inView, onView]);

    return (
        <S.CampaignCardMiniWrapper
            id={campaignId}
            href={destination}
            bgTransparent={bgTransparent}
            onClick={onClick}
            ref={ref}
        >
            <S.ImageWrapper withLogo={false}>
                <S.CampaignImage src={media.url} alt={media.altText} />
            </S.ImageWrapper>
            <S.ContentWrapper isPlusVariant={isPlusVariant}>
                <S.CampaignTitle isPlusVariant={isPlusVariant}>{campaign.name}</S.CampaignTitle>

                {/* There are multiple design variants of CampaignCardMini components,
                    some without discount or without timeleft component
                */}

                {!hideDiscount && (
                    <>
                        <S.SpacerXS />
                        <Discount campaign={campaign} isPlusVariant={isPlusVariant} />
                    </>
                )}
                {!hideTimeLeft && (
                    <>
                        <S.SpacerS />
                        <TimeLeft dateTill={dateEnd} />
                    </>
                )}
            </S.ContentWrapper>
        </S.CampaignCardMiniWrapper>
    );
};

export { CampaignCardMini };
